import { MANIA_LANE_COUNT, type ManiaLane } from '@shared/maniaNote';
import {
  HIT_LINE_Y_FRAC,
  LANE_GAP_PX,
  LANE_STACK_HORIZONTAL_PAD_PX,
  PLAYING_FIELD_MAX_WIDTH_PX,
  TILE_HEIGHT_FRAC,
  TILE_HEIGHT_MAX_PX,
  TILE_HEIGHT_MIN_PX,
} from './render/pianoRenderConstants';

/**
 * Shared playfield geometry for Piano. Used by both the renderer
 * (to draw lanes, the hit line, tiles, and HUD chrome anchored to
 * the playing field) and the input source (to project pointer
 * events to a lane). Keeping the math in one module stops the two
 * layers from diverging — a divergence would let taps land on a
 * different lane from the visual tile the player sees.
 *
 * Mirrors `circlesPlayfieldGeometry.ts` for the Circles mode.
 */

/**
 * Cached layout geometry derived from the current canvas CSS size.
 * The renderer recomputes this once per frame at the top of draw()
 * and threads it through every helper that needs lane / tile /
 * hit-line positions so the math lives in one place. The input
 * source recomputes it per pointer event (events are sparse
 * compared to frames, so the extra layout work is negligible).
 */
export interface PianoLayout {
  /** Left edge of lane 0 in canvas CSS px. */
  lane0X: number;
  /** Width of one lane in canvas CSS px (4 lanes total). */
  laneWidth: number;
  /** Total width of the lane stack including inter-lane gaps. */
  laneStackWidth: number;
  /** Y coordinate of the hit line. */
  hitLineY: number;
  /** Pixel height of one tap tile (chunky vertical proportion). */
  tileHeight: number;
}

export function computeLayout(canvasWidth: number, canvasHeight: number): PianoLayout {
  const playingFieldWidth = Math.min(canvasWidth, PLAYING_FIELD_MAX_WIDTH_PX);
  const playingFieldX = Math.round((canvasWidth - playingFieldWidth) / 2);
  const lane0X = playingFieldX + LANE_STACK_HORIZONTAL_PAD_PX;
  const laneStackWidth = playingFieldWidth - LANE_STACK_HORIZONTAL_PAD_PX * 2;
  const laneWidth =
    (laneStackWidth - LANE_GAP_PX * (MANIA_LANE_COUNT - 1)) / MANIA_LANE_COUNT;
  const hitLineY = canvasHeight * HIT_LINE_Y_FRAC;
  const tileHeight = Math.max(
    TILE_HEIGHT_MIN_PX,
    Math.min(TILE_HEIGHT_MAX_PX, canvasHeight * TILE_HEIGHT_FRAC),
  );
  return { lane0X, laneWidth, laneStackWidth, hitLineY, tileHeight };
}

/**
 * Project a pointer event's client-x to a lane index, or null if
 * the tap landed outside the visible lane stack.
 *
 * The lane stack — i.e. the run of 4 lane-coloured rectangles —
 * is the only area that visually reads as "playing field" to the
 * player. The dark margin on either side (whether it's the
 * letterbox outside the centred playing field, or the small
 * padding inside the playing field) is the same `palette.bg` as
 * the page background, so a click there is plainly off-target.
 * Returning null for those clicks stops the engine from racking up
 * empty-tap penalties from misclicks that landed nowhere near a
 * tile.
 *
 * Within the lane stack we DO snap clicks that land in the 4 px
 * inter-lane gaps to the lane immediately to their left — those
 * gaps are decorative and too narrow to demand pixel-perfect
 * accuracy from touch input.
 *
 * Y is deliberately not gated: lanes are painted full canvas
 * height (see `drawLaneBackgrounds`) and the HUD chrome sits on
 * top of lane 0 / lane 3's columns, so every Y inside the canvas
 * still corresponds to a legitimate lane intent.
 */
export function laneFromClientX(
  canvas: HTMLCanvasElement,
  clientX: number,
): ManiaLane | null {
  const rect = canvas.getBoundingClientRect();
  if (rect.width <= 0) {
    return null;
  }
  const layout = computeLayout(rect.width, rect.height);
  const localX = clientX - rect.left;
  if (localX < layout.lane0X || localX > layout.lane0X + layout.laneStackWidth) {
    return null;
  }
  const slot = layout.laneWidth + LANE_GAP_PX;
  const rawIdx = Math.floor((localX - layout.lane0X) / slot);
  const idx = Math.max(0, Math.min(MANIA_LANE_COUNT - 1, rawIdx));
  return idx as ManiaLane;
}
